import { useState, useEffect } from "react";
import { QuantityControl } from "../../helper/QuantityControl";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CHECKOUT, ERROR, SUCCESS } from "../../include/constant";
import { ratingAvg1 } from "../../include/function";
import { getCustomer, setToLocalStorage } from "../../include/function";
import { useNotification } from "../../Toast/ToastProvider";
import { useCart } from "../context/UserCartCountContext";
import { api, baseURL, customerReqConfig } from "../../include/api";
import "../../styles/Cart.css";
import { CircleX, SaveIcon, ShoppingCart, Trash } from "lucide-react";
import SuggestedForYou from "../SuggestedForYou";
import { loadCartData } from "../../helper/cartUtils";
import ItemMissed from "../ItemMissed";
import RecentlyView from "../RecentlyView";
import Sponsored from "../Sponsored";

const CartSection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useNotification();
  const customer = getCustomer();
  const { fetchCartCount } = useCart();
  const [allReviewData, setAllReviewData] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [placeOrderLoading, setPlaceOrderLoading] = useState(false);
  const [savedForLaterData, setSavedForLaterData] = useState({});
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(null);
  const [showSavedItemRemoveModal, setShowSavedItemRemoveModal] =
    useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const calculateProductPrice = (item) => {
    const isB2C = !customer || customer.customerType === "individual";

    // ✅ LOGIC 2: qtyAndPrice - Use productPrice as MRP, price as Selling Price
    if (item.qtyAndPrice && item.qtyAndPrice.length > 0) {
      // Find the matching qty range based on item's variantId or minQty
      let selectedEntry;

      if (item.variantId) {
        selectedEntry = item.qtyAndPrice.find(
          (q) => String(q.id) === String(item.variantId),
        );
      }
      if (!selectedEntry) {
        selectedEntry = item.qtyAndPrice.find(
          (q) => String(q.minQty) === String(item.qty),
        );
      }
      if (!selectedEntry) {
        selectedEntry = item.qtyAndPrice.reduce(
          (min, current) => (current.minQty < min.minQty ? current : min),
          item.qtyAndPrice[0],
        );
      }

      if (selectedEntry) {
        // ✅ price = Selling Price (from cart), productPrice = MRP (from product)
        const sellingPrice = parseFloat(item.price) || 0;
        const mrp =
          parseFloat(item.productPrice) || parseFloat(item.price) || 0;

        return {
          displayPrice: sellingPrice.toFixed(2), // ✅ Selling Price (270.00)
          qtyRange: `${selectedEntry.minQty} - ${selectedEntry.maxQty} ${selectedEntry.basedOn}`,
          originalPrice: mrp.toFixed(2), // ✅ MRP (325.00)
          discountInfo: null, // ✅ NO discount for qtyAndPrice
          priceType: "qtyAndPrice",
          basedOn: selectedEntry.basedOn,
          minQtyValue: selectedEntry.minQty,
        };
      }
    }

    // ✅ LOGIC 3: productVariant - Calculate from retail + discount
    if (item.productVariant && item.productVariant.length > 0) {
      const retailPrices = item.productVariant
        .map((variant) => parseFloat(variant.price) || 0)
        .filter((price) => !isNaN(price) && price > 0);

      const sellingPrices = item.productVariant
        .map((variant) => {
          const variantPrice = parseFloat(variant.price) || 0;
          let finalPrice = variantPrice;
          const discMode = isB2C
            ? (variant.b2cDiscountMode || "").toLowerCase()
            : (variant.b2bDiscountMode || "").toLowerCase();
          const discValue = isB2C
            ? parseFloat(variant.b2cDiscountValue) || 0
            : parseFloat(variant.b2bDiscountValue) || 0;

          if (discMode === "percent") {
            finalPrice = variantPrice - (variantPrice * discValue) / 100;
          } else if (discMode === "amount") {
            finalPrice = variantPrice - discValue;
          }
          return Math.max(0, finalPrice);
        })
        .filter((price) => !isNaN(price) && price > 0);

      if (retailPrices.length > 0 && sellingPrices.length > 0) {
        const minRetail = Math.min(...retailPrices);
        const maxRetail = Math.max(...retailPrices);
        const minSelling = Math.min(...sellingPrices);
        const maxSelling = Math.max(...sellingPrices);

        return {
          retailPriceDisplay:
            retailPrices.length > 1
              ? `₹ ${minRetail.toFixed(2)} - ${maxRetail.toFixed(2)}`
              : `₹ ${minRetail.toFixed(2)}`,
          sellingPriceDisplay:
            sellingPrices.length > 1
              ? `₹ ${minSelling.toFixed(2)} - ${maxSelling.toFixed(2)}`
              : `₹ ${minSelling.toFixed(2)}`,
          priceType: "productVariant",
        };
      }
    }

    // ✅ LOGIC 1: Simple Product - Apply discount to base price
    const basePrice = parseFloat(item.price) || 0;
    const discMode = isB2C
      ? (item.b2cDiscountMode || "").toLowerCase()
      : (item.b2bDiscountMode || "").toLowerCase();
    const discValue = isB2C
      ? parseFloat(item.b2cDiscountValue) || 0
      : parseFloat(item.b2bDiscountValue) || 0;

    let finalPrice = basePrice;
    if (discMode === "percent") {
      finalPrice = basePrice - (basePrice * discValue) / 100;
    } else if (discMode === "amount") {
      finalPrice = basePrice - discValue;
    }
    finalPrice = Math.max(0, finalPrice);

    const discountInfo =
      parseFloat(discValue) > 0 && discMode
        ? {
            value: discMode === "percent" ? discValue : discValue,
            mode: discMode === "percent" ? "%" : "₹",
          }
        : null;

    return {
      displayPrice: finalPrice.toFixed(2),
      originalPrice: basePrice.toFixed(2),
      discountInfo: discountInfo,
      priceType: "basePrice",
    };
  };

  const fetchcart = () => {
    api
      .get("/cart/?customerId=" + customer.id, customerReqConfig())
      .then((response) => {
        if (response.status === 200 && response?.data?.data) {
          setCart(response.data.data);
        }
      })
      .catch(() => {});
  };

  const handleIncreaseCount = (item) => {
    setLoading(item.productId);
    if (!cart[0] || !cart[0].cartItems) return;
    const cartItemIndex = cart[0].cartItems.findIndex(
      (c) => String(c.productId) === String(item.productId),
    );
    if (cartItemIndex === -1 || !cart[0].cartItems[cartItemIndex]) return;
    const currentItem = cart[0].cartItems[cartItemIndex];
    currentItem.qty = Number(currentItem.qty || 0) + 1;
    let productsDisValue = 0;
    if (item.discountMode === "percent") {
      productsDisValue +=
        (parseFloat(item.discountValue) / 100) * parseFloat(item.price);
    } else if (item.discountMode === "amount") {
      productsDisValue += parseFloat(item.discountValue);
    }
    const itemsPrice = Number(cart[0].itemsPrice) + parseFloat(item.price);
    const totalDiscountValue =
      parseFloat(cart[0].totalDiscountValue || 0) + productsDisValue;
    const totalDeliveryCharge = parseFloat(cart[0].totalDeliveryCharge || 0);
    const data = {
      customerId: cart[0].customerId,
      itemsPrice,
      totalDiscountValue,
      discountMode: item.discountMode,
      totalDeliveryCharge,
      totalPrice: itemsPrice - totalDiscountValue + totalDeliveryCharge,
      cartItems: [...cart[0].cartItems],
    };
    api
      .put(`/cart/?id=${cart[0].id}`, data, customerReqConfig())
      .then((response) => {
        if (response.status === 200) {
          toast({ type: SUCCESS, message: response.data.message });
          fetchcart();
          handleRemoveItemFromSavedlist();
          fetchCartCount();
        }
      })
      .catch((error) => {
        toast({
          type: ERROR,
          message: error.response?.data?.message || error.message,
        });
      })
      .finally(() => {
        setLoading(null);
      });
  };

  const handleDecreaseCount = (item) => {
    setLoading(item.productId);
    if (!cart[0] || !cart[0].cartItems) return;
    const cartItemIndex = cart[0].cartItems.findIndex(
      (c) => String(c.productId) === String(item.productId),
    );
    if (cartItemIndex === -1 || !cart[0].cartItems[cartItemIndex]) return;
    const currentItem = cart[0].cartItems[cartItemIndex];
    if (currentItem.qty <= 1) return;
    currentItem.qty -= 1;
    let productsDisValue = 0;
    if (item.discountMode === "percent") {
      productsDisValue =
        (parseFloat(item.discountValue) / 100) * parseFloat(item.price);
    } else if (item.discountMode === "amount") {
      productsDisValue = parseFloat(item.discountValue);
    }
    const itemsPrice = Number(cart[0].itemsPrice) - parseFloat(item.price);
    const totalDiscountValue =
      parseFloat(cart[0].totalDiscountValue || 0) - productsDisValue;
    const totalDeliveryCharge = parseFloat(cart[0].totalDeliveryCharge || 0);
    const data = {
      customerId: cart[0].customerId,
      itemsPrice,
      totalDiscountValue,
      discountMode: cart[0].discountMode,
      totalDeliveryCharge,
      totalPrice: itemsPrice - totalDiscountValue + totalDeliveryCharge,
      cartItems: [...cart[0].cartItems],
    };
    api
      .put(`/cart/?id=${cart[0].id}`, data, customerReqConfig())
      .then((response) => {
        if (response.status === 200) {
          toast({ type: SUCCESS, message: response.data.message });
          fetchcart();
          handleRemoveItemFromSavedlist();
          fetchCartCount();
        }
      })
      .catch((error) => {
        toast({
          type: ERROR,
          message: error.response?.data?.message || error.message,
        });
      })
      .finally(() => {
        setLoading(null);
      });
  };

  const handleRemoveItem = async (item) => {
    if (!cart || cart.length === 0) return;
    const updatedItems = cart[0].cartItems.filter(
      (c) => String(c.productId) !== String(item.productId),
    );
    const safeFloat = (val) => {
      const num = parseFloat(val);
      return isNaN(num) ? 0 : num;
    };
    if (updatedItems.length === 0) {
      setCart([]);
      handleRemoveItemFromSavedlist?.();
      try {
        const response = await api.delete(
          `/cart/?id=${cart[0].id}`,
          customerReqConfig(),
        );
        toast({ type: SUCCESS, message: response.data.message });
        fetchcart();
        fetchCartCount();
      } catch (error) {
        toast({
          type: ERROR,
          message: error.response?.data?.message || error.message,
        });
        fetchcart();
      } finally {
        setLoading(null);
      }
    } else {
      const itemsPrice = updatedItems.reduce(
        (sum, p) => sum + safeFloat(p.price) * parseInt(p.qty || 1),
        0,
      );
      const totalDiscountValue = updatedItems.reduce((sum, p) => {
        let d = 0;
        if (p.discountMode === "percent") {
          d =
            (safeFloat(p.discountValue) / 100) *
            safeFloat(p.price) *
            parseInt(p.qty || 1);
        } else if (p.discountMode === "amount") {
          d = safeFloat(p.discountValue) * parseInt(p.qty || 1);
        }
        return sum + d;
      }, 0);
      const totalDeliveryCharge = updatedItems.reduce(
        (sum, p) => sum + safeFloat(p.deliveryCharge) * parseInt(p.qty || 1),
        0,
      );
      const updatedCart = {
        customerId: cart[0].customerId,
        itemsPrice,
        totalDiscountValue,
        discountMode: cart[0].discountMode,
        totalDeliveryCharge,
        totalPrice: itemsPrice - totalDiscountValue + totalDeliveryCharge,
        cartItems: updatedItems,
      };
      setCart([updatedCart]);
      try {
        const response = await api.put(
          `/cart/?id=${cart[0].id}`,
          updatedCart,
          customerReqConfig(),
        );
        toast({ type: SUCCESS, message: response.data.message });
        fetchcart();
        fetchCartCount();
      } catch (error) {
        toast({
          type: ERROR,
          message: error.response?.data?.message || error.message,
        });
        fetchcart();
      } finally {
        setLoading(null);
      }
    }
  };

  const getSavedItems = () => {
    setFetchLoading(true);
    api
      .get("/save_for_later/?customerId=" + customer.id, customerReqConfig())
      .then((response) => {
        if (response.status === 200) {
          setSavedForLaterData(response?.data?.data);
          console.log("get-saveForLater", response.data.data);
        }
      })
      .catch((error) => {
        setSavedForLaterData({});
      })
      .finally(() => setFetchLoading(false));
  };

  useEffect(() => {
    fetchcart();
    getSavedItems();
  }, []);

  useEffect(() => {
    api
      .get("/rating_and_review/", customerReqConfig())
      .then((response) => {
        if (response.status === 200) {
          setAllReviewData(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching reviews:", error);
      });
  }, []);

  const handleCheckout = async () => {
    setLoading("checkout");
    let inactiveProducts = cart[0].cartItems.filter(
      (f) => f.status === "inactive" || f.shopStatus === "inactive",
    );
    if (inactiveProducts.length === 0) {
      const orderItems = cart[0].cartItems.map((item) => ({
        ...item,
        total: item.qty * item.price,
      }));
      const checkoutData = {
        totalItems: cart[0].cartItems.length,
        billAmount: cart[0].itemsPrice,
        totalDiscount: cart[0].totalDiscountValue,
        totalDeliveryCharge: cart[0].totalDeliveryCharge,
        totalAmount: cart[0].totalPrice,
        orderItems: orderItems,
      };
      setPlaceOrderLoading(true);
      await setToLocalStorage(CHECKOUT, checkoutData);
      setTimeout(() => {
        setPlaceOrderLoading(false);
        setLoading(null);
        navigate("/Checkout");
      }, 1000);
    } else {
      setLoading(null);
      toast({
        type: ERROR,
        message: "Please remove unavailable products from your cart",
      });
    }
  };

  const updateCartItemQtyLocally = (productId, newQty) => {
    setCart((prev) => {
      const updated = [...prev];
      if (updated[0] && updated[0].cartItems) {
        updated[0].cartItems = updated[0].cartItems.map((item) =>
          item.productId === productId ? { ...item, qty: newQty } : item,
        );
      }
      return updated;
    });
  };

  const addToCart = async (product, qty = 1) => {
    try {
      let latestSaved = savedForLaterData?.products || [];
      if (!latestSaved.length) {
        const savedRes = await api.get(
          `/save_for_later/?customerId=${customer.id}`,
          customerReqConfig(),
        );
        latestSaved = savedRes?.data?.data?.products || [];
      }
      const isInSaved = latestSaved.some(
        (p) => String(p.id) === String(product.id),
      );
      if (isInSaved) {
        await api.delete(
          `/save_for_later/?customerId=${customer.id}&productId=${product.id}`,
          customerReqConfig(),
        );
        setSavedForLaterData((prev) => ({
          ...prev,
          products:
            prev.products?.filter((p) => String(p.id) !== String(product.id)) ||
            [],
        }));
      }
      const itemsPrice = parseFloat(product.price) * qty;
      const deliveryCharge = parseFloat(product.deliveryCharge || 0) * qty;
      let discountValue = 0;
      if (product.discountMode === "percent") {
        discountValue =
          (parseFloat(product.discountValue) / 100) *
          parseFloat(product.price) *
          qty;
      } else if (product.discountMode === "amount") {
        discountValue = parseFloat(product.discountValue) * qty;
      }
      const newCartItem = {
        productId: product.id,
        qty,
        price: product.price,
        discountMode: product.discountMode,
        discountValue: product.discountValue,
        deliveryCharge: product.deliveryCharge || 0,
        name: product.name,
        mainImage: product.mainImage,
        status: product.status,
        shopStatus: product.shopStatus,
      };
      if (cart.length > 0 && cart[0]?.cartItems?.length > 0) {
        const updatedCart = { ...cart[0] };
        const alreadyInCart = updatedCart.cartItems.some(
          (item) => String(item.productId) === String(product.id),
        );
        if (alreadyInCart) {
          toast({ type: ERROR, message: "Product is already in the cart" });
          return false;
        }
        updatedCart.cartItems.push(newCartItem);
        updatedCart.itemsPrice += itemsPrice;
        updatedCart.totalDiscountValue += discountValue;
        updatedCart.totalDeliveryCharge += deliveryCharge;
        updatedCart.totalPrice =
          updatedCart.itemsPrice -
          updatedCart.totalDiscountValue +
          updatedCart.totalDeliveryCharge;
        await api.put(
          `/cart/?id=${updatedCart.id}`,
          updatedCart,
          customerReqConfig(),
        );
        setCart([updatedCart]);
      } else {
        const payload = {
          customerId: customer.id,
          itemsPrice,
          totalPrice: itemsPrice - discountValue + deliveryCharge,
          totalDiscountValue: discountValue,
          discountMode: product.discountMode,
          totalDeliveryCharge: deliveryCharge,
          cartItems: [newCartItem],
        };
        const res = await api.post("/cart/", payload, customerReqConfig());
        if (res.data?.data) {
          setCart([res.data.data]);
        }
      }
      fetchCartCount();
      setTimeout(fetchcart, 300);
      return true;
    } catch (error) {
      toast({
        type: ERROR,
        message: error.response?.data?.message || error.message,
      });
      throw error;
    }
  };

  const handleMoveToCart = async (product) => {
    setLoading(product.id);
    try {
      const res = await api.get(
        `/product/?id=${product.id}`,
        customerReqConfig(),
      );
      if (res.status === 200 && res.data?.data?.[0]) {
        const fullProduct = res.data.data[0];
        await addToCart(fullProduct, 1);
        toast({ type: SUCCESS, message: "Added to cart" });
        setSavedForLaterData((prev) => ({
          ...prev,
          products: prev.products.filter((p) => p.id !== fullProduct.id),
        }));
        await api.delete(
          `/save_for_later/?customerId=${customer.id}&productId=${fullProduct.id}`,
          customerReqConfig(),
        );
      }
    } catch (error) {
      toast({
        type: ERROR,
        message: error.response?.data?.message || error.message,
      });
    } finally {
      setLoading(null);
    }
  };

  const handleSaveForLater = async (ItemToRemove) => {
    setLoading(ItemToRemove.productId);
    const payload = {
      customerId: customer.id,
      productId: ItemToRemove.productId,
    };
    const fullItem = cart[0]?.cartItems?.find(
      (c) => c.productId === ItemToRemove.productId,
    );
    if (!fullItem)
      setCart((prev) => {
        const updated = [...prev];
        if (updated[0]) {
          updated[0].cartItems = updated[0].cartItems.filter(
            (c) => c.productId !== ItemToRemove.productId,
          );
          const safeFloat = (val) =>
            isNaN(parseFloat(val)) ? 0 : parseFloat(val);
          const itemsPrice = updated[0].cartItems.reduce(
            (sum, p) => sum + safeFloat(p.price) * parseInt(p.qty || 1),
            0,
          );
          const totalDiscountValue = updated[0].cartItems.reduce((sum, p) => {
            let d = 0;
            if (p.discountMode === "percent")
              d =
                (safeFloat(p.discountValue) / 100) *
                safeFloat(p.price) *
                parseInt(p.qty || 1);
            else if (p.discountMode === "amount")
              d = safeFloat(p.discountValue) * parseInt(p.qty || 1);
            return sum + d;
          }, 0);
          const totalDeliveryCharge = updated[0].cartItems.reduce(
            (sum, p) =>
              sum + safeFloat(p.deliveryCharge) * parseInt(p.qty || 1),
            0,
          );
          updated[0].itemsPrice = itemsPrice;
          updated[0].totalDiscountValue = totalDiscountValue;
          updated[0].totalDeliveryCharge = totalDeliveryCharge;
          updated[0].totalPrice =
            itemsPrice - totalDiscountValue + totalDeliveryCharge;
        }
        return updated;
      });
    setSavedForLaterData((prev) => ({
      ...prev,
      products: [...(prev?.products || []), fullItem],
    }));
    try {
      const res = await api.post(
        "/save_for_later/",
        payload,
        customerReqConfig(),
      );
      toast({ type: SUCCESS, message: res.data.message });
      await handleRemoveItem(ItemToRemove);
      getSavedItems();
      const updatedCartItems =
        cart[0]?.cartItems?.filter(
          (c) => c.productId !== ItemToRemove.productId,
        ) || [];
      const safeFloat = (val) => (isNaN(parseFloat(val)) ? 0 : parseFloat(val));
      const itemsPrice = updatedCartItems.reduce(
        (sum, p) => sum + safeFloat(p.price) * parseInt(p.qty || 1),
        0,
      );
      const totalDiscountValue = updatedCartItems.reduce((sum, p) => {
        let d = 0;
        if (p.discountMode === "percent")
          d =
            (safeFloat(p.discountValue) / 100) *
            safeFloat(p.price) *
            parseInt(p.qty || 1);
        else if (p.discountMode === "amount")
          d = safeFloat(p.discountValue) * parseInt(p.qty || 1);
        return sum + d;
      }, 0);
      const totalDeliveryCharge = updatedCartItems.reduce(
        (sum, p) => sum + safeFloat(p.deliveryCharge) * parseInt(p.qty || 1),
        0,
      );
      const updatedCart = {
        customerId: cart[0]?.customerId,
        itemsPrice,
        totalDiscountValue,
        discountMode: cart[0]?.discountMode,
        totalDeliveryCharge,
        totalPrice: itemsPrice - totalDiscountValue + totalDeliveryCharge,
        cartItems: updatedCartItems,
      };
      await api.put(
        `/cart/?id=${cart[0]?.id}`,
        updatedCart,
        customerReqConfig(),
      );
      fetchCartCount();
    } catch (error) {
      toast({
        type: ERROR,
        message: error.response?.data?.message || error.message,
      });
    } finally {
      setLoading(null);
    }
  };

  const handleRemoveItemFromSavedlist = (productId) => {
    if (!customer.id || !productId) return;
    setLoading(productId);
    api
      .delete(
        `/save_for_later/?customerId=${customer.id}&productId=${productId}`,
        customerReqConfig(),
      )
      .then((response) => {
        toast({ type: SUCCESS, message: response.data.message });
        setSavedForLaterData((prev) => ({
          ...prev,
          products: prev.products?.filter((p) => p.id !== productId) || [],
        }));
      })
      .catch((error) => {
        toast({
          type: ERROR,
          message: error.response?.data?.message || error.message,
        });
      })
      .finally(() => {
        setLoading(null);
      });
  };

  useEffect(() => {
    if (location.hash === "#saved-for-later") {
      const savedSection = document.querySelector(
        '[data-section="saved-for-later"]',
      );
      if (savedSection) {
        savedSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location.hash]);

  // const allCartItems = cart.flatMap((c) => c.cartItems || []);
  // const safeFloat = (val) => (isNaN(parseFloat(val)) ? 0 : parseFloat(val));
  // const b2bDiscount = allCartItems.reduce((sum, item) => {
  //   let d = 0;
  //   const mode = (item.b2bDiscountMode || "").toLowerCase();
  //   const value = item.b2bDiscountValue || 0;
  //   if (mode === "percent") {
  //     d = Math.round(
  //       (safeFloat(value) / 100) *
  //         safeFloat(item.price) *
  //         parseInt(item.qty || 1),
  //     );
  //   } else if (mode === "amount") {
  //     d = Math.round(safeFloat(value) * parseInt(item.qty || 1));
  //   }
  //   return sum + d;
  // }, 0);
  // const b2cDiscount = allCartItems.reduce((sum, item) => {
  //   let d = 0;
  //   const mode = (item.b2cDiscountMode || "").toLowerCase();
  //   const value = item.b2cDiscountValue || 0;
  //   if (mode === "percent") {
  //     d = Math.round(
  //       (safeFloat(value) / 100) *
  //         safeFloat(item.price) *
  //         parseInt(item.qty || 1),
  //     );
  //   } else if (mode === "amount") {
  //     d = Math.round(safeFloat(value) * parseInt(item.qty || 1));
  //   }
  //   return sum + d;
  // }, 0);
  // const delivery = allCartItems.reduce(
  //   (sum, item) =>
  //     sum + safeFloat(item.deliveryCharge) * parseInt(item.qty || 1),
  //   0,
  // );
  // const totalMRP = allCartItems.reduce((sum, item) => {
  //   const price = parseInt(item.price || 0);
  //   const qty = parseInt(item.qty || 1);
  //   return sum + price * qty;
  // }, 0);
  // const discount =
  //   !customer || customer.customerType === "individual"
  //     ? b2cDiscount.toFixed(2)
  //     : b2bDiscount.toFixed(2);
  // const subtotal = totalMRP - discount;
  // const grandTotal = subtotal;
  // ✅ FIXED: Calculate totals correctly for qtyAndPrice
  const allCartItems = cart.flatMap((c) => c.cartItems || []);
  const safeFloat = (val) => (isNaN(parseFloat(val)) ? 0 : parseFloat(val));

  // Calculate discount - SKIP for qtyAndPrice items
  const totalDiscount = allCartItems.reduce((sum, item) => {
    let d = 0;
    const mode = (item.discountMode || "").toLowerCase();
    const value = parseFloat(item.discountValue) || 0;
    const pPrice = parseFloat(item.price) || 0;
    const pQty = parseInt(item.qty || 1);

    // ✅ Only calculate discount if discountMode exists (NOT for qtyAndPrice)
    if (mode === "percent" && item.discountMode) {
      d = (value / 100) * pPrice * pQty;
    } else if (mode === "amount" && item.discountMode) {
      d = value * pQty;
    }
    return sum + d;
  }, 0);

  const delivery = allCartItems.reduce(
    (sum, item) =>
      sum + safeFloat(item.deliveryCharge) * parseInt(item.qty || 1),
    0,
  );

  // ✅ totalMRP = Sum of (price × qty) for all items
  const totalMRP = allCartItems.reduce((sum, item) => {
    const price = parseFloat(item.price) || 0;
    const qty = parseInt(item.qty || 1);
    return sum + price * qty;
  }, 0);

  const discount = totalDiscount.toFixed(2);
  const subtotal = totalMRP - totalDiscount; // For qtyAndPrice, discount=0
  const grandTotal = subtotal + delivery;

  const [hotDeals, setHotDeals] = useState([]);
  const [dayDeals, setDayDeals] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [brandList, setBrandList] = useState([]);
  const [dealsOfTheDayCategoryId, setDealsOfTheDayCategoryId] = useState(null);
  const [cartData, setCartData] = useState([]);
  const [addedToCartMap, setAddedToCartMap] = useState({});
  const [bestSellers, setBestSellers] = useState([]);
  const [bestSellerCategoryId, setBestSellerCategoryId] = useState(null);
  const [hotDealsCategoryId, setHotDealsCategoryId] = useState(null);

  useEffect(() => {
    const handleCartUpdate = () => {
      loadCartData(customer.id, setCartData, setAddedToCartMap);
    };
    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => window.removeEventListener("cartUpdated", handleCartUpdate);
  }, []);

  useEffect(() => {
    getCategory();
    getHomeCategory();
    getBrand();
  }, []);

  const getCategory = () => {
    setFetchLoading(true);
    api
      .get("/category/?parentId=0")
      .then((response) => {
        if (response.status === 200) {
          setCategoryList(
            response.data.data.filter((cl) => cl.name !== "Home"),
          );
        }
      })
      .catch(() => {
        setCategoryList([]);
      })
      .finally(() => setFetchLoading(false));
  };

  const getBrand = () => {
    setFetchLoading(true);
    api
      .get("/category/?parentId=-1")
      .then((response) => {
        if (response.status === 200) {
          setBrandList(response.data.data.filter((cl) => cl.name !== "Home"));
        }
      })
      .catch(() => {
        setBrandList([]);
      })
      .finally(() => setFetchLoading(false));
  };

  const getHomeCategory = () => {
    setFetchLoading(true);
    api
      .get("/category/?flag=getHomeAndChilds")
      .then((response) => {
        if (response.status === 200) {
          const Allitems = response.data.data[0].childs;
          const deals1 = Allitems.find(
            (item) => item.name === "Deals Of The Day",
          );
          const deals2 = Allitems.find((item) => item.name === "Hot Deals");
          const deals3 = Allitems.find((item) => item.name === "Best Sellers");
          const deals4 =
            Allitems.find((item) => item.name === "New Arrivals")?.products ||
            [];
          if (deals1) setDealsOfTheDayCategoryId(deals1.id);
          if (deals2) setHotDealsCategoryId(deals2.id);
          if (deals3) setBestSellerCategoryId(deals3.id);
          setDayDeals(deals1?.products || []);
          setHotDeals(deals2?.products || []);
          setBestSellers(deals3?.products || []);
        }
      })
      .catch(() => {})
      .finally(() => setFetchLoading(false));
  };

  return (
    <>
      {cart[0]?.cartItems?.length > 0 ? (
        <section className="cart py-48">
          <div className="container container-lg">
            <div className="checkout-steps d-flex justify-content-center align-items-center mb-4 pb-48 px-80">
              <div className="step active">1</div>
              <div className="word">Cart</div>
              <div className="line"></div>
              <div className="step">2</div>
              <div>Address</div>
              <div className="line"></div>
              <div className="step">3</div>
              <div>Order Summary</div>
              <div className="line"></div>
              <div className="step">4</div>
              <div>Payment</div>
            </div>
            <div className="row gy-2">
              <div className="col-xl-9 col-lg-8">
                <div className="cart-table border border-gray-100 rounded-16 px-40 py-40">
                  <div className="overflow-x-auto scroll-sm scroll-sm-horizontal">
                    <div className="d-flex justify-content-between">
                      <h6 className="text-xl mb-32 fw-bold">SHOPPING CART</h6>
                      <h6 className="text-xl mb-32 fw-bold">{`${allCartItems.length} ITEMS`}</h6>
                    </div>
                    <div className="cart-items-list">
                      {cart.map((cart) =>
                        cart.cartItems.map((cartItem) => (
                          <CartItem
                            key={cartItem.productId}
                            cartItem={cartItem}
                            cart={cart}
                            customer={customer}
                            handleIncreaseCount={handleIncreaseCount}
                            handleDecreaseCount={handleDecreaseCount}
                            handleRemoveItem={handleRemoveItem}
                            handleSaveForLater={handleSaveForLater}
                            allReviewData={allReviewData}
                            fetchcart={fetchcart}
                            updateCartItemQtyLocally={updateCartItemQtyLocally}
                            loading={loading}
                            setLoading={setLoading}
                            calculateProductPrice={calculateProductPrice}
                          />
                        )),
                      )}
                    </div>
                  </div>
                  <div className="flex-between flex-wrap gap-16 mt-32">
                    <div className="flex-align gap-16">
                      <input
                        type="text"
                        className="common-input"
                        placeholder="Enter Coupon Code"
                        style={{ height: "10px", width: "200px" }}
                      />
                      <button
                        type="submit"
                        className="btn btn-main btn-sm w-100 rounded-5 px-40"
                      >
                        Apply
                      </button>
                    </div>
                    <span>
                      <button
                        onClick={handleCheckout}
                        className="btn btn-main w-100 rounded-5 px-40 py-12"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            />
                            <span className="visually-hidden">Loading...</span>{" "}
                            Loading...
                          </>
                        ) : (
                          "Proceed to checkout"
                        )}
                      </button>
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-lg-4">
                <div className="cart-sidebar border border-gray-100 rounded-16 px-24 py-40">
                  <h6 className="text-xl mb-32">PRICE DETAILS</h6>
                  <div className="bg-color-three rounded-8 p-24">
                    <div className="mb-32 flex-between gap-8">
                      <span className="text-gray-900 font-heading-two">
                        {`Price (${allCartItems.length}) items`}
                      </span>
                      <span className="text-gray-900 fw-semibold">
                        ₹ {totalMRP.toFixed(2)}
                      </span>
                    </div>
                    <div className="mb-32 flex-between gap-8">
                      <span className="text-gray-900 font-heading-two">
                        Discount
                      </span>
                      <span className="text-gray-900 fw-semibold">
                        - ₹ {discount}
                      </span>
                    </div>
                    <div className="mb-32 flex-between gap-8">
                      <span className="text-gray-900 font-heading-two">
                        Coupon Discount
                      </span>
                      <span className="text-gray-900 fw-semibold">
                        - ₹ 0.00
                      </span>
                    </div>
                  </div>
                  <div className="bg-color-three rounded-8 p-24 mt-24">
                    <div className="flex-between gap-8">
                      <span className="text-gray-900 text-xl fw-bold">
                        Total
                      </span>
                      <span className="text-gray-900 text-xl fw-bold">
                        ₹ {grandTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div className="mt-68">
                    You will save ₹ {discount} on this order
                  </div>
                </div>
                <div className="mt-16 ms-16">
                  Your personal data will be used to process your order, support
                  your experience throughout this website, and for other purpose
                  described in our{" "}
                  <a style={{ color: "#ff6600" }}>Privacy policy</a> .
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="cart py-80">
          <div className="container container-lg text-center">
            <h3>Your cart is empty</h3>
          </div>
        </section>
      )}
      {savedForLaterData?.products?.length > 0 && (
        <div className="row gy-2 px-16 mb-32" data-section="saved-for-later">
          <div className="col-xl-9 col-lg-8">
            <div className="cart-table border border-gray-100 rounded-16 px-40 py-40">
              <div className="overflow-x-auto scroll-sm scroll-sm-horizontal">
                <div className="d-flex justify-content-between">
                  <h6 className="text-xl mb-32 fw-bold">SAVE FOR LATER</h6>
                  <h6 className="text-xl mb-32 fw-bold">{`${savedForLaterData?.products?.length} ITEMS`}</h6>
                </div>
                <div className="cart-items-list">
                  {savedForLaterData.products.map((p, index) => {
                    const productReviews =
                      allReviewData?.filter((r) => r.productId === p.id) || [];
                    const priceInfo = calculateProductPrice(p);

                    return (
                      <>
                        {showSavedItemRemoveModal && (
                          <div
                            className="modal-overlay"
                            onClick={() => setShowSavedItemRemoveModal(null)}
                          >
                            <div
                              className="modal-content"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span
                                onClick={() =>
                                  setShowSavedItemRemoveModal(null)
                                }
                                className="d-flex justify-content-end"
                              >
                                <CircleX color="#ddd" />
                              </span>
                              <h6 className="modal-header text-xl mb-16 fw-bold w-100 d-flex">
                                Remove Item
                              </h6>
                              <div className="modal-body">
                                <p
                                  style={{ color: "#aaa7a7ff" }}
                                  className="mb-40"
                                >
                                  Are you sure you want to remove this item?
                                </p>
                                <div className="d-flex hstack gap-8">
                                  <button
                                    className="btn btn-main btn-sm rounded-5 py-16 fw-normal w-50"
                                    onClick={() => {
                                      handleRemoveItemFromSavedlist(
                                        showSavedItemRemoveModal.productId,
                                      );
                                      setShowSavedItemRemoveModal(null);
                                    }}
                                  >
                                    REMOVE
                                  </button>
                                  <button
                                    className="btn btn-outline-main btn-sm rounded-5 py-16 fw-normal w-50"
                                    onClick={() =>
                                      setShowSavedItemRemoveModal(null)
                                    }
                                  >
                                    CANCEL
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="card cart-table border border-gray-100 rounded-16 mb-3 p-16">
                          <div className="row g-0">
                            <div
                              className="col-12 col-md-3 d-flex align-items-center justify-content-center rounded-16 p-3 mb-3 mb-md-0"
                              style={{
                                backgroundColor: "#F8F8F8",
                                width: "20%",
                              }}
                            >
                              <Link to={`/product-details/${p.productId}`}>
                                <img
                                  src={baseURL + "/" + p.mainImage}
                                  alt={p.name}
                                  className="img-fluid rounded-8"
                                  style={{
                                    maxHeight: "120px",
                                    objectFit: "contain",
                                  }}
                                />
                              </Link>
                            </div>
                            <div className="col-12 col-md-6 border border-gray-100 border-start-0 border-md-start-0 border-end-1 border-top-0 border-bottom-0 border-md-end-0 p-0 mb-3 mb-md-0">
                              <div className="card-body d-flex flex-column h-100">
                                <div className="flex-grow-1">
                                  <h6 className="card-title text-md fw-bold mb-2">
                                    <Link
                                      to={`/product-details/${p.productId}`}
                                      className="link text-line-2"
                                    >
                                      {p.name.toUpperCase()}
                                    </Link>
                                  </h6>
                                  {productReviews.length > 0 && (
                                    <div className="flex-align gap-8 mb-2">
                                      <span className="fw-medium text-neutral-600">
                                        <div className="badge bg-success">
                                          {ratingAvg1(productReviews)}{" "}
                                          <i className="ph-fill ph-star" />
                                        </div>
                                      </span>
                                      <span className="fw-medium text-gray-500">
                                        ({productReviews.length})
                                      </span>
                                    </div>
                                  )}
                                  <br />
                                  <div className="d-flex hstack gap-8">
                                    {p.variant !== "" ? (
                                      <>
                                        <div className="text-gray-400 text-md fw-semibold text-decoration-line-through mb-2">
                                          {priceInfo.retailPriceDisplay}
                                        </div>
                                        <div className="text-lg fw-bold">
                                          {priceInfo.sellingPriceDisplay}
                                        </div>
                                      </>
                                    ) : priceInfo.priceType ===
                                      "qtyAndPrice" ? (
                                      <>
                                        {/* ✅ qtyAndPrice: Show Selling Price (price) + MRP (productPrice) */}
                                        <div className="text-lg fw-bold">
                                          ₹ {priceInfo.displayPrice}{" "}
                                          {/* ✅ 270.00 */}
                                        </div>
                                        {priceInfo.qtyRange && (
                                          <span className="text-gray-500 fw-normal ms-2">
                                            ({priceInfo.qtyRange})
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        {/* Simple product with discount */}
                                        <div className="text-lg fw-bold">
                                          ₹ {priceInfo.displayPrice}
                                        </div>
                                        {priceInfo.discountInfo && (
                                          <span
                                            className="text-sm"
                                            style={{ color: "#ff6600" }}
                                          >
                                            Discount of{" "}
                                            {priceInfo.discountInfo.value}
                                            {priceInfo.discountInfo.mode}
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </div>

                                  {/* ✅ MRP / Base Price Display */}
                                  {priceInfo.priceType === "qtyAndPrice" ? (
                                    <>
                                      <label className="text-sm fw-medium">
                                        {" "}
                                        {`M.R.P :\u00A0 `}
                                      </label>
                                      <span
                                        className="text-gray-500 text-md fw-bold"
                                        style={{
                                          textDecoration: "line-through",
                                        }}
                                      >
                                        ₹{" "}
                                        {parseFloat(
                                          priceInfo.originalPrice || 0,
                                        ).toFixed(2)}{" "}
                                        {/* ✅ 325.00 */}
                                      </span>
                                    </>
                                  ) : priceInfo.priceType !== "basePrice" ? (
                                    // For productVariant, show retail price range if available
                                    priceInfo.retailPriceDisplay && (
                                      <>
                                        <label className="text-sm fw-medium">
                                          {" "}
                                          {`M.R.P :\u00A0 `}
                                        </label>
                                        <span
                                          className="text-gray-500 text-md fw-bold"
                                          style={{
                                            textDecoration: "line-through",
                                          }}
                                        >
                                          {priceInfo.retailPriceDisplay}
                                        </span>
                                      </>
                                    )
                                  ) : (
                                    // For simple products
                                    <>
                                      <label className="text-sm fw-medium">
                                        {" "}
                                        {`M.R.P :\u00A0 `}
                                      </label>
                                      <span
                                        className="text-gray-500 text-md fw-bold"
                                        style={{
                                          textDecoration: "line-through",
                                        }}
                                      >
                                        ₹{" "}
                                        {parseFloat(
                                          priceInfo.originalPrice || 0,
                                        ).toFixed(2)}
                                      </span>
                                    </>
                                  )}

                                  <div className="text-gray-500 text-md fw-medium">
                                    Inclusive of all taxes
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-3 d-flex flex-column align-items-end ps-16">
                              <div>
                                <button
                                  className="btn btn-sm text-neutral-600 flex-center hover-bg-main-600 hover-text-white mt-32 mb-16"
                                  style={{
                                    width: "200px",
                                    backgroundColor: "#ddd",
                                    border: "none",
                                  }}
                                  onClick={() => handleMoveToCart(p)}
                                  disabled={loading === p.id}
                                >
                                  {loading ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm"
                                        role="status"
                                        aria-hidden="true"
                                      ></span>
                                      Adding...
                                    </>
                                  ) : (
                                    <>
                                      Add to Cart
                                      {`\u00A0\u00A0\u00A0`}
                                      <span>
                                        <ShoppingCart size={"20px"} />
                                      </span>
                                    </>
                                  )}
                                </button>
                              </div>
                              <div>
                                <button
                                  className="btn btn-sm text-neutral-600 flex-center hover-bg-main-600 hover-text-white"
                                  style={{
                                    width: "200px",
                                    backgroundColor: "#ddd",
                                    border: "none",
                                  }}
                                  onClick={() => {
                                    setShowSavedItemRemoveModal({
                                      productId: p.id,
                                      name: p.name,
                                    });
                                  }}
                                  disabled={loading === p.id}
                                >
                                  {loading ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm"
                                        role="status"
                                        aria-hidden="true"
                                      ></span>
                                      Removing...
                                    </>
                                  ) : (
                                    <>
                                      Remove
                                      {`\u00A0\u00A0\u00A0`}
                                      <span>
                                        <Trash size={"20px"} />
                                      </span>
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        <style jsx global>
                          {`
                            .modal-overlay {
                              position: fixed;
                              top: 0;
                              left: 0;
                              right: 0;
                              bottom: 0;
                              background-color: rgba(0, 0, 0, 0.5);
                              display: flex;
                              justify-content: center;
                              align-items: center;
                              z-index: 1000;
                            }
                            .modal-content {
                              background: white;
                              padding: 24px;
                              border-radius: 12px;
                              width: 90%;
                              max-width: 400px;
                              box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
                            }
                            .modal-header {
                              display: flex;
                              justify-content: space-between;
                              align-items: center;
                              margin-bottom: 16px;
                            }
                            .close-btn {
                              font-size: 24px;
                              cursor: pointer;
                              background: none;
                              border: none;
                              color: #666;
                            }
                            .modal-body {
                              text-align: center;
                            }
                            .btn {
                              padding: 8px 24px;
                              border-radius: 8px;
                              font-weight: bold;
                              cursor: pointer;
                            }
                            .btn-danger {
                              background-color: #ff6600;
                              color: white;
                            }
                            .btn-secondary {
                              background-color: #ddd;
                              color: black;
                            }
                            .ms-2 {
                              margin-left: 8px;
                            }
                          `}
                        </style>
                      </>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <SuggestedForYou
        key={`FeaturedOne-${reloadKey}`}
        hotDeals={hotDeals}
        hotDealsCategoryId={hotDealsCategoryId}
        addedToCartMap={addedToCartMap}
        setCartData={setCartData}
        setAddedToCartMap={setAddedToCartMap}
      />
      <ItemMissed
        key={`DealsOne-${reloadKey}`}
        dayDeals={dayDeals}
        categoryList={categoryList}
        dealsOfTheDayCategoryId={dealsOfTheDayCategoryId}
        addedToCartMap={addedToCartMap}
        setCartData={setCartData}
        setAddedToCartMap={setAddedToCartMap}
      />
      <RecentlyView
        key={`TopSellingOne-${reloadKey}`}
        bestSellers={bestSellers}
        bestSellerCategoryId={bestSellerCategoryId}
        addedToCartMap={addedToCartMap}
        setCartData={setCartData}
        setAddedToCartMap={setAddedToCartMap}
      />
      <Sponsored
        categoryList={categoryList}
        addedToCartMap={addedToCartMap}
        setCartData={setCartData}
        setAddedToCartMap={setAddedToCartMap}
      />
    </>
  );
};

export default CartSection;

const CartItem = ({
  cartItem,
  customer,
  handleRemoveItem,
  handleSaveForLater,
  allReviewData,
  fetchcart,
  cart,
  updateCartItemQtyLocally,
  loading,
  setLoading,
  calculateProductPrice,
}) => {
  const [quantity, setQuantity] = useState(cartItem.qty);
  const [saveLaterLoading, setSaveLaterLoading] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(null);

  const productReviews =
    allReviewData?.filter((r) => r.productId === cartItem.productId) || [];

  useEffect(() => {
    if (cartItem.qty !== quantity) {
      setQuantity(cartItem.qty);
    }
  }, [cartItem.qty, quantity]);

  const updateCartDirectly = (newQty) => {
    setLoading(cartItem.productId);
    api
      .get(`/cart/?customerId=${cart.customerId}`, customerReqConfig())
      .then((res) => {
        if (res.status === 200 && res.data?.data?.[0]) {
          const fetchedCart = res.data.data[0];
          const updatedItems = fetchedCart.cartItems.map((item) =>
            item.productId === cartItem.productId
              ? { ...cartItem, qty: newQty }
              : item,
          );
          const safeFloat = (val) =>
            isNaN(parseFloat(val)) ? 0 : parseFloat(val);
          const itemsPrice = updatedItems.reduce(
            (sum, p) => sum + safeFloat(p.price) * parseInt(p.qty || 1),
            0,
          );
          const totalDiscountValue = updatedItems.reduce((sum, p) => {
            let d = 0;
            if (p.discountMode === "percent") {
              d =
                (safeFloat(p.discountValue) / 100) *
                safeFloat(p.price) *
                parseInt(p.qty || 1);
            } else if (p.discountMode === "amount") {
              d = safeFloat(p.discountValue) * parseInt(p.qty || 1);
            }
            return sum + d;
          }, 0);
          const totalDeliveryCharge = updatedItems.reduce(
            (sum, p) =>
              sum + safeFloat(p.deliveryCharge) * parseInt(p.qty || 1),
            0,
          );
          const totalPrice =
            itemsPrice - totalDiscountValue + totalDeliveryCharge;
          const payload = {
            customerId: fetchedCart.customerId,
            itemsPrice,
            totalDiscountValue,
            discountMode: fetchedCart.discountMode,
            totalDeliveryCharge,
            totalPrice,
            cartItems: updatedItems,
          };
          return api.put(
            `/cart/?id=${fetchedCart.id}`,
            payload,
            customerReqConfig(),
          );
        }
      })
      .then(() => {
        fetchcart();
      })
      .catch((e) => {
        console.error("Qty update error", e);
      })
      .finally(() => {
        setLoading(null);
      });
  };

  const handleManualQtyChange = (newQty) => {
    if (newQty <= 0 || isNaN(newQty)) return;
    setQuantity(newQty);
    updateCartDirectly(newQty);
  };

  const incrementQuantity = () => {
    if (loading) return;
    setLoading(cartItem.productId);
    const newQty = quantity + 1;
    setQuantity(newQty);
    updateCartDirectly(newQty);
  };

  const decrementQuantity = () => {
    if (loading || quantity <= 1) return;
    setLoading(cartItem.productId);
    const newQty = quantity - 1;
    setQuantity(newQty);
    updateCartDirectly(newQty);
  };

  const removeItem = () => {
    setShowRemoveModal({
      productId: cartItem.productId,
      name: cartItem.name,
    });
  };

  const handleSaveClick = async () => {
    setSaveLaterLoading(true);
    try {
      await handleSaveForLater(cartItem);
    } catch (err) {
      console.error("Failed to save item for later", err);
    } finally {
      setSaveLaterLoading(false);
    }
  };

  // ✅ Use calculateProductPrice for consistent pricing
  const priceInfo = calculateProductPrice
    ? calculateProductPrice(cartItem)
    : (() => {
        const isB2C = !customer || customer.customerType === "individual";
        const basePrice = parseFloat(cartItem.price) || 0;
        const b2bMode = (cartItem.b2bDiscountMode || "").toLowerCase();
        const b2cMode = (cartItem.b2cDiscountMode || "").toLowerCase();

        let b2bDiscountUnitPrice = basePrice;
        if (b2bMode === "percent") {
          const disc = parseFloat(cartItem.b2bDiscountValue) || 0;
          b2bDiscountUnitPrice = basePrice - (basePrice * disc) / 100;
        } else if (b2bMode === "amount") {
          const disc = parseFloat(cartItem.b2bDiscountValue) || 0;
          b2bDiscountUnitPrice = basePrice - disc;
        }

        let b2cDiscountUnitPrice = basePrice;
        if (b2cMode === "percent") {
          const disc = parseFloat(cartItem.b2cDiscountValue) || 0;
          b2cDiscountUnitPrice = basePrice - (basePrice * disc) / 100;
        } else if (b2cMode === "amount") {
          const disc = parseFloat(cartItem.b2cDiscountValue) || 0;
          b2cDiscountUnitPrice = basePrice - disc;
        }

        const finalPrice = isB2C ? b2cDiscountUnitPrice : b2bDiscountUnitPrice;
        const displayPrice = Math.max(0, Math.round(finalPrice)).toFixed(2);
        const custDiscVal = isB2C
          ? cartItem.b2cDiscountValue
          : cartItem.b2bDiscountValue;
        const custDiscMode = isB2C
          ? b2cMode === "percent"
            ? "%"
            : "₹"
          : b2bMode === "percent"
            ? "%"
            : "₹";

        return {
          displayPrice,
          originalPrice: basePrice.toFixed(2),
          discountInfo:
            parseFloat(custDiscVal) > 0
              ? { value: custDiscVal, mode: custDiscMode }
              : null,
          priceType: "basePrice",
        };
      })();
  console.log(cartItem);
  return (
    <div className="card cart-table border border-gray-100 rounded-16 px-1 py-16 mb-32">
      <div className="row g-0">
        <div
          className="col-12 col-md-3 d-flex align-items-center justify-content-center rounded-16 p-3 mb-3 mb-md-0"
          style={{
            backgroundColor: "#F8F8F8",
            width: "20%",
            marginLeft: "20px",
          }}
        >
          <Link to={`/product-details/${cartItem.productId}`}>
            <img
              src={baseURL + "/" + cartItem.mainImage}
              alt={cartItem.name}
              className="img-fluid rounded-8"
              style={{ maxHeight: "120px", objectFit: "contain" }}
            />
          </Link>
        </div>
        <div className="col-12 col-md-6 border border-gray-100 border-start-0 border-md-start-0 border-end-1 border-top-0 border-bottom-0 border-md-end-0 p-0 mb-3 mb-md-0">
          <div className="card-body d-flex flex-column h-100">
            <div className="flex-grow-1">
              <h6 className="card-title text-md fw-bold mb-2">
                <Link
                  to={`/product-details/${cartItem.productId}`}
                  className="link text-line-2"
                >
                  {cartItem.name?.toUpperCase()}
                  {cartItem.variantName && (
                    <span className="text-gray-500 fw-normal ms-2">
                      ({cartItem.variantName})
                    </span>
                  )}
                </Link>
              </h6>
              {productReviews.length > 0 && (
                <div className="d-flex align-items-center gap-8 mb-2">
                  <span className="fw-medium text-neutral-600">
                    <div className="badge bg-success">
                      {ratingAvg1(productReviews)}{" "}
                      <i className="ph-fill ph-star" />
                    </div>
                  </span>
                  <span className="fw-medium text-gray-500">
                    ({productReviews.length})
                  </span>
                </div>
              )}
              <br />
              <div className="d-flex hstack gap-8">
                {cartItem.variant !== "" ? (
                  <>
                    <div className="text-gray-400 text-md fw-semibold text-decoration-line-through mb-2">
                      {priceInfo.retailPriceDisplay}
                    </div>
                    <div className="text-lg fw-bold">
                      {priceInfo.sellingPriceDisplay}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-lg fw-bold">
                      ₹ {priceInfo.displayPrice}
                      {priceInfo.qtyRange && (
                        <span className="text-gray-500 fw-normal ms-2">
                          ({priceInfo.qtyRange})
                        </span>
                      )}
                    </div>
                    {priceInfo.discountInfo && (
                      <span className="text-sm" style={{ color: "#ff6600" }}>
                        Discount of {priceInfo.discountInfo.value}{" "}
                        {priceInfo.discountInfo.mode}
                      </span>
                    )}
                  </>
                )}
              </div>
              <label className="text-sm fw-medium"> {`M.R.P :\u00A0 `}</label>
              <span
                className="text-gray-500 text-md fw-bold"
                style={{ textDecoration: "line-through" }}
              >
                ₹ {parseFloat(cartItem.price || 0).toFixed(2)}
              </span>
              <div className="text-gray-500 text-md fw-medium">
                Inclusive of all taxes
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3 d-flex flex-column align-items-end ps-32">
          <div className="w-100 mb-16">
            <QuantityControl
              quantity={quantity}
              setQuantity={handleManualQtyChange}
              incrementQuantity={incrementQuantity}
              decrementQuantity={decrementQuantity}
              buttonsDisabled={!!loading}
              isLoadingInc={loading === cartItem.productId}
              isLoadingDec={loading === cartItem.productId}
            />
          </div>
          <div className="w-100 mb-16">
            <button
              className="btn btn-sm text-neutral-600 flex-center hover-bg-main-600 hover-text-white w-100"
              style={{
                maxWidth: "200px",
                backgroundColor: "#ddd",
                border: "none",
              }}
              onClick={handleSaveClick}
              disabled={saveLaterLoading}
            >
              {saveLaterLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Saving...
                </>
              ) : (
                <>
                  Save for later
                  {`\u00A0\u00A0\u00A0`}
                  <span>
                    <SaveIcon size={"20px"} />
                  </span>
                </>
              )}
            </button>
          </div>
          <div className="w-100">
            <button
              className="btn btn-sm text-neutral-600 flex-center hover-bg-main-600 hover-text-white w-100"
              style={{
                maxWidth: "200px",
                backgroundColor: "#ddd",
                border: "none",
              }}
              onClick={removeItem}
              disabled={saveLaterLoading || !!loading}
            >
              {saveLaterLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Removing...
                </>
              ) : (
                <>
                  Remove
                  {`\u00A0\u00A0\u00A0`}
                  <span>
                    <Trash size={"20px"} />
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
        {showRemoveModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowRemoveModal(null)}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <span
                onClick={() => setShowRemoveModal(null)}
                className="d-flex justify-content-end"
              >
                <CircleX color="#ddd" />
              </span>
              <h6 className="modal-header text-xl mb-16 fw-bold w-100 d-flex">
                Remove Item
              </h6>
              <div className="modal-body">
                <p style={{ color: "#aaa7a7ff" }} className="mb-40">
                  Are you sure you want to remove this item?
                </p>
                <div className="d-flex hstack gap-8">
                  <button
                    className="btn btn-main btn-sm rounded-5 py-16 fw-normal w-50"
                    onClick={() => {
                      handleRemoveItem(cartItem);
                      setShowRemoveModal(null);
                    }}
                  >
                    REMOVE
                  </button>
                  <button
                    className="btn btn-outline-main btn-sm rounded-5 py-16 fw-normal w-50"
                    onClick={() => setShowRemoveModal(null)}
                  >
                    CANCEL
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <style jsx>{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }
          .modal-content {
            background: white;
            padding: 24px;
            border-radius: 12px;
            width: 90%;
            max-width: 400px;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
          }
          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 16px;
          }
          .close-btn {
            font-size: 24px;
            cursor: pointer;
            background: none;
            border: none;
            color: #666;
          }
          .modal-body {
            text-align: center;
          }
          .btn {
            padding: 8px 24px;
            border-radius: 8px;
            font-weight: bold;
            cursor: pointer;
          }
          .btn-danger {
            background-color: #ff6600;
            color: white;
          }
          .btn-secondary {
            background-color: #ddd;
            color: black;
          }
          .ms-2 {
            margin-left: 8px;
          }
        `}</style>
      </div>
    </div>
  );
};
